/**
 * Which tabs the signed-in person sees.
 *
 * Pure and in its own module so it can be tested directly. Importing `App.tsx`
 * from a spec drags in MSAL and every page with it, which is why the previous
 * guard over this file read App.tsx as TEXT and asserted regexes — a test that
 * passes while the behaviour is wrong. This is the behaviour.
 */

export interface NavTab {
  path: string;
  label: string;
}

export const BASE_TABS: NavTab[] = [
  // Today is first — it is the screen a PM or Estimator opens at 7am. It is
  // NOT the default landing route; '/' still redirects to /projects.
  { path: '/today', label: 'Today' },
  { path: '/projects',  label: 'Projects'  },
  { path: '/schedule',  label: 'Schedule'  },
  { path: '/manpower',  label: 'Manpower'  },
  { path: '/equipment', label: 'Equipment' },
  { path: '/survey',    label: 'Survey'    },
  { path: '/receiving', label: 'Receiving' },
  { path: '/shipping',  label: 'Shipping'  },
  { path: '/contracts', label: 'Contracts' },
  { path: '/qaqc',      label: 'QA/QC'     },
  { path: '/safety',    label: 'Safety'    },
  { path: '/daily-logs', label: 'Daily Logs' },
  { path: '/reports',   label: 'Reports'   },
  { path: '/ppr',       label: 'PPR'       },
];

export interface NavAccess {
  isAdmin: boolean;
  /**
   * Holds `feedback.manage` (the Feedback Admin role) — may open the Admin tab,
   * where the page then shows the Feedback sub-tab and nothing else.
   *
   * ⚠️ A SECOND, INDEPENDENT REASON TO SEE THE ADMIN TAB, and not a step toward
   * `isAdmin`. It must never be folded into it: `isAdmin` is what unlocks every
   * other sub-tab, and the API (not this flag) is what keeps this role out of
   * them. See `visibleAdminTabs`.
   *
   * This is the RAW grant: Admin arrives through `isAdmin` and field leaders are
   * handled by `isFieldLeaderOnly`, both in `visibleTabs`. `Access.canManageFeedback`
   * in HelpDrawer shares the name but has those rules folded in.
   */
  canManageFeedback: boolean;
  canViewReports: boolean;
  canViewPpr: boolean;
  /** Holds contracts.view or contracts.edit (Admin always) — may open the Contracts page. */
  canViewContracts: boolean;
  /**
   * May open the Shipping page: Admin, `receiving.edit` (Logistics and the PMs who raise requests) or `projects.view_all`.
   * The list across jobs carries onsite contacts' phone numbers, so unlike Receiving it is not open to every
   * signed-in user: the API answers 403 to anyone else (`ShippingService.canReadCompanyWide`). Anyone can still
   * read a job's own requests on that job's Shipping tab.
   */
  canViewShipping: boolean;
  /** Has at least one live project assignment — see `hasActiveAssignments`. */
  canViewToday: boolean;
  /**
   * Holds `today.leadership` — may open the company-wide leadership view.
   *
   * ⚠️ A SECOND, INDEPENDENT REASON TO SEE THE TAB, never a replacement for the
   * first. `canViewToday` is pure assignment with no Admin and no view-all
   * bypass, which is right for a page called "your own work" and is exactly why
   * it locks out the people this view is for: measured 2026-09-19, EIGHT of the
   * eleven leadership-role holders had no tab and no route at all. Widening
   * `canViewToday` itself would have been the wrong fix — it would start showing
   * an empty personal page to people with no jobs. So the two are OR'd here and
   * kept apart everywhere else.
   */
  canViewLeadershipToday: boolean;
  isFieldLeaderOnly: boolean;
}

export function visibleTabs(a: NavAccess): NavTab[] {
  // Field leaders get a single tab — Projects (scoped to their assignments).
  if (a.isFieldLeaderOnly) return BASE_TABS.filter((tab) => tab.path === '/projects');
  return [
    ...BASE_TABS.filter((tab) =>
      (tab.path !== '/reports' || a.canViewReports) &&
      (tab.path !== '/ppr' || a.canViewPpr) &&
      (tab.path !== '/contracts' || a.canViewContracts) &&
      (tab.path !== '/shipping' || a.canViewShipping) &&
      (tab.path !== '/today' || a.canViewToday || a.canViewLeadershipToday)),
    ...(a.isAdmin || a.canManageFeedback ? [{ path: '/admin', label: '⚙ Admin' }] : []),
  ];
}

/**
 * What the `/today` route should render for this viewer.
 *
 * `'leadership'` is a fourth outcome rather than a flag on `'page'` because the
 * two render different components against different endpoints.
 */
export type TodayRouteState = 'loading' | 'redirect' | 'page' | 'leadership';

/**
 * The route-level half of the Today gate.
 *
 * ⚠️ Hiding the tab is not the gate. Nothing stops somebody typing `/today` or
 * following a bookmark from before they handed their jobs over, so the route
 * has to make the same decision independently. Pure and here rather than inline
 * in `App.tsx` so it can be tested: the tab filter was extracted for exactly
 * that reason and this half was left behind, which a review caught.
 *
 * `'loading'` is distinct from `'redirect'` on purpose. `me` is undefined until
 * `/users/me` resolves, and treating that as ineligible would bounce a PM who
 * deep-links to Today straight back to Projects before the answer arrives.
 */
export function todayRouteState(a: {
  /** False until `/users/me` has resolved. */
  meLoaded: boolean;
  isFieldLeaderOnly: boolean;
  canViewToday: boolean;
  canViewLeadershipToday: boolean;
  /**
   * `'company'` when the URL asks for the leadership view (`/today?view=company`).
   *
   * ⚠️ A REQUEST, NOT A GRANT. Somebody without the permission who types the
   * parameter falls through to their own page rather than getting an error —
   * there is nothing to deny them, they simply do not have the other view.
   */
  requestedView?: 'company' | null;
}): TodayRouteState {
  if (!a.meLoaded) return 'loading';
  // Field leaders keep their single-tab experience. Checked FIRST so it cannot
  // be overridden by a permission — a field-leader-only account cannot hold
  // `today.leadership` today, and if that ever changes this rule should still win.
  if (a.isFieldLeaderOnly) return 'redirect';
  /**
   * ⚠️ ASSIGNMENTS WIN OVER THE LEADERSHIP VIEW, and that is the product owner's call
   * (2026-09-19), not a technical default.
   *
   * A few people hold both. Opening them on the company roll-up would REPLACE
   * the 7am screen they already use with something they did not ask for — the
   * heaviest-loaded of them would sign in and eleven jobs would be gone. So
   * they land on their own work and switch to the company view deliberately.
   * Taking away a working screen to promote a new one is how people learn to
   * distrust a tool.
   *
   * Who they are, and their loads, are in `docs/today-leadership-proposal.md`
   * §3.1 — not here, because this file is bundled and its comments are
   * published. See the note on `sourcemap` in `vite.config.ts`.
   */

  const wantsCompany = a.requestedView === 'company' && a.canViewLeadershipToday;
  if (a.canViewToday && !wantsCompany) return 'page';
  return a.canViewLeadershipToday ? 'leadership' : 'redirect';
}

/**
 * What the `/admin` route should render for this viewer.
 *
 * The route-level half of the Admin tab's gate, pure so it can be tested — the same
 * reason `todayRouteState` exists. Hiding the tab does not stop a typed URL or a
 * bookmark, so the route decides independently.
 *
 * ⚠️ `'loading'` IS NOT `'redirect'`, and conflating them was a bug. `me` is
 * undefined until `/users/me` resolves, so `isAdmin` and `canManageFeedback` are
 * both false for that moment. The route used to be REGISTERED only once one of them
 * was true; until then the catch-all redirect matched, and a refresh or bookmark on
 * /admin landed on /projects before the answer arrived. Every sibling gated route
 * registers itself unconditionally and decides in its element. Admin had this too —
 * a Feedback Admin, whose whole job lives on this page, would hit it on every reload.
 */
export type AdminRouteState = 'loading' | 'redirect' | 'page';

export function adminRouteState(a: {
  /** False until `/users/me` has resolved. */
  meLoaded: boolean;
  isFieldLeaderOnly: boolean;
  isAdmin: boolean;
  canManageFeedback: boolean;
}): AdminRouteState {
  if (!a.meLoaded) return 'loading';
  // Same rule as the tab: a field-leader-only account has Projects alone, whatever
  // it has been granted. Never true for Admin, who is an office user.
  if (a.isFieldLeaderOnly) return 'redirect';
  return a.isAdmin || a.canManageFeedback ? 'page' : 'redirect';
}
